/**
 * 工序模块常量定义
 * 
 * @description 定义工序相关的常量、标签映射、选项列表等
 * @module types/process-operation/constants
 * @category ProcessOperation - 工序管理
 * 
 * @backend-source
 * - Model: backend/app/Modules/ProcessRoute/Models/ProcessOperation.php
 * 
 * @update-trigger
 * - 后端枚举映射变更时
 * - 新增枚举值时
 * 
 * @last-updated 2025-10-31
 * @status ✅ 已验证
 */

import { ProcessOperationType, ProcessInspectionType, ProcessOperationStatus } from './enums';

/**
 * 工序类型标签映射
 * 
 * @backend ProcessOperation::$operationTypeMap
 */
export const PROCESS_OPERATION_TYPE_LABELS: Record<ProcessOperationType, string> = {
  [ProcessOperationType.PROCESSING]: '加工',
  [ProcessOperationType.ASSEMBLY]: '装配',
  [ProcessOperationType.INSPECTION]: '检验',
  [ProcessOperationType.PACKAGING]: '包装',
  [ProcessOperationType.TRANSPORT]: '运输',
  [ProcessOperationType.OTHER]: '其他',
};

/**
 * 工序类型选项列表（用于下拉选择）
 */
export const PROCESS_OPERATION_TYPE_OPTIONS = Object.entries(PROCESS_OPERATION_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/**
 * 质检类型标签映射
 * 
 * @backend ProcessOperation::$inspectionTypeMap
 */
export const PROCESS_INSPECTION_TYPE_LABELS: Record<ProcessInspectionType, string> = {
  [ProcessInspectionType.NONE]: '无',
  [ProcessInspectionType.FIRST]: '首检',
  [ProcessInspectionType.PATROL]: '巡检',
  [ProcessInspectionType.FINAL]: '终检',
  [ProcessInspectionType.FULL]: '全检',
};

/**
 * 质检类型选项列表（用于下拉选择）
 */
export const PROCESS_INSPECTION_TYPE_OPTIONS = Object.entries(PROCESS_INSPECTION_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/**
 * 工序状态标签映射
 * 
 * @backend ProcessOperation::$statusMap
 */
export const PROCESS_OPERATION_STATUS_LABELS: Record<ProcessOperationStatus, string> = {
  [ProcessOperationStatus.ACTIVE]: '启用',
  [ProcessOperationStatus.INACTIVE]: '停用',
  [ProcessOperationStatus.OBSOLETE]: '淘汰',
};

/**
 * 工序状态选项列表（用于下拉选择）
 */
export const PROCESS_OPERATION_STATUS_OPTIONS = Object.entries(PROCESS_OPERATION_STATUS_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/**
 * 工序状态标签映射（带颜色）
 * 
 * @description 用于 Tag 组件显示
 */
export const PROCESS_OPERATION_STATUS_TAGS: Record<
  ProcessOperationStatus,
  { text: string; color: string }
> = {
  [ProcessOperationStatus.ACTIVE]: { text: '启用', color: 'green' },
  [ProcessOperationStatus.INACTIVE]: { text: '停用', color: 'default' },
  [ProcessOperationStatus.OBSOLETE]: { text: '淘汰', color: 'red' },
};

/**
 * 工序类型标签映射（带颜色）
 * 
 * @description 用于 Tag 组件显示
 */
export const PROCESS_OPERATION_TYPE_TAGS: Record<
  ProcessOperationType,
  { text: string; color: string }
> = {
  [ProcessOperationType.PROCESSING]: { text: '加工', color: 'blue' },
  [ProcessOperationType.ASSEMBLY]: { text: '装配', color: 'green' },
  [ProcessOperationType.INSPECTION]: { text: '检验', color: 'orange' },
  [ProcessOperationType.PACKAGING]: { text: '包装', color: 'purple' },
  [ProcessOperationType.TRANSPORT]: { text: '运输', color: 'cyan' },
  [ProcessOperationType.OTHER]: { text: '其他', color: 'default' },
};

/**
 * 质检类型标签映射（带颜色）
 * 
 * @description 用于 Tag 组件显示
 */
export const PROCESS_INSPECTION_TYPE_TAGS: Record<
  ProcessInspectionType,
  { text: string; color: string }
> = {
  [ProcessInspectionType.NONE]: { text: '无', color: 'default' },
  [ProcessInspectionType.FIRST]: { text: '首检', color: 'blue' },
  [ProcessInspectionType.PATROL]: { text: '巡检', color: 'green' },
  [ProcessInspectionType.FINAL]: { text: '终检', color: 'orange' },
  [ProcessInspectionType.FULL]: { text: '全检', color: 'red' },
};

